import { useState, useEffect } from 'react';

/**
 * Formats a date/timestamp string or Date object into IST (Asia/Kolkata) format.
 * @param {string|number|Date} dateInput
 * @param {'full'|'date'|'time'|'short'|'relative'} mode
 * @returns {string}
 */
export function formatIST(dateInput, mode = 'short') {
  if (!dateInput) return '-';
  
  let date;
  if (typeof dateInput === 'string') {
    let s = dateInput.trim();
    if (s.includes(' ') && !s.includes('T')) {
      s = s.replace(' ', 'T');
    }
    if (s.includes('T') && !s.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(s) && !/[+-]\d{4}$/.test(s)) {
      s += 'Z';
    }
    date = new Date(s);
  } else {
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return '-';

  if (mode === 'relative') {
    return getRelativeIST(date);
  }

  const optionsMap = {
    full: {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    },
    date: {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
    time: {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    },
    short: {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    },
  };

  const options = optionsMap[mode] || optionsMap.short;
  try {
    const formatted = new Intl.DateTimeFormat('en-IN', options).format(date);
    if (mode === 'full') {
      return `${formatted} IST`;
    }
    return formatted;
  } catch (err) {
    return date.toLocaleString();
  }
}

function getRelativeIST(date) {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 45) return 'Just now';
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;

  return formatIST(date, 'date');
}

/**
 * Custom React hook to get periodically updated relative IST time string.
 * @param {string|number|Date} dateInput
 * @param {number} intervalMs - Recalculate frequency in milliseconds (default 60000ms)
 * @returns {string}
 */
export function useRelativeTime(dateInput, intervalMs = 60000) {
  const [relativeText, setRelativeText] = useState(() => formatIST(dateInput, 'relative'));

  useEffect(() => {
    setRelativeText(formatIST(dateInput, 'relative'));
    if (!dateInput) return;

    const timer = setInterval(() => {
      setRelativeText(formatIST(dateInput, 'relative'));
    }, intervalMs);

    return () => clearInterval(timer);
  }, [dateInput, intervalMs]);

  return relativeText;
}
